import * as styles from "./JobEditorBody.css";

import { clsx } from "clsx";
import { useSetAtom } from "jotai";
import { type JSX, type MouseEvent, useState } from "react";

import { Badge } from "@frontend/components/atoms/Badge";
import { Button } from "@frontend/components/atoms/Button";
import { Icon } from "@frontend/components/atoms/Icon";
import { Spinner } from "@frontend/components/atoms/Spinner";
import {
  Tabs,
  TabsContent,
  TabsIndicator,
  TabsList,
  TabsPanel,
  TabsTab,
  stickyTabsBar,
} from "@frontend/components/atoms/Tabs";
import { useArrayField } from "@frontend/hooks/form";
import { Trans, useLingui } from "@lingui/react/macro";

import { addTaskAtom, jobForm } from "../stores/editor";
import { JobFields } from "./JobFields";
import { TaskList } from "./TaskList";

export type JobEditorBodyProps = {
  loading: boolean;
  error: string | null;
  conflict: boolean;
  stickyTabs?: boolean;
};

export function JobEditorBody({
  loading,
  error,
  conflict,
  stickyTabs = false,
}: JobEditorBodyProps): JSX.Element {
  const { t } = useLingui();

  const tasks = useArrayField(jobForm.fields.tasks);
  const addTask = useSetAtom(addTaskAtom);

  const [tab, setTab] = useState("main");

  function add(event: MouseEvent<HTMLElement>) {
    addTask(event.currentTarget.getBoundingClientRect());
    setTab("tasks");
  }

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner aria-label={t`Loading`} />
      </div>
    );
  }

  return (
    <div className={styles.body}>
      {error ? (
        <p className={styles.alert} role="alert">
          {conflict ? (
            <Trans>
              Someone else saved this job while it was open. Reopen it to see
              their version, then make your change again.
            </Trans>
          ) : (
            error
          )}
        </p>
      ) : null}

      <Tabs className={styles.tabs} value={tab} onValueChange={setTab}>
        <div className={clsx(styles.tabsHeader, stickyTabs && stickyTabsBar)}>
          <TabsList>
            <TabsTab value="main" icon="Users">
              <Trans>Work post</Trans>
            </TabsTab>

            <TabsTab value="tasks" icon="ListPlus">
              <Trans>Tasks</Trans>

              {0 === tasks.count ? null : (
                <Badge variant="neutral" size="sm" dot={false}>
                  {tasks.count}
                </Badge>
              )}
            </TabsTab>

            <TabsIndicator />
          </TabsList>

          <Button
            variant="ghost"
            size="sm"
            leftSection={<Icon name="Plus" size="sm" />}
            onClick={add}
          >
            {t`Add a task`}
          </Button>
        </div>

        <TabsContent className={styles.tabsContent}>
          <TabsPanel value="main">
            <JobFields />
          </TabsPanel>

          <TabsPanel value="tasks" className={styles.tasksPanel}>
            <TaskList />
          </TabsPanel>
        </TabsContent>
      </Tabs>
    </div>
  );
}
